import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import BackToAll from "@/components/feature/BackToAll";
import Seo, { getAbsoluteUrl } from "@/components/feature/Seo";
import { buildBreadcrumbSchema, type BreadcrumbItem } from "@/components/feature/Breadcrumbs";
import { buildFaqPageSchema } from "@/components/feature/FaqAccordion";
import EnquirySection from "@/components/feature/EnquirySection";
import EnquiryModal from "@/components/feature/EnquiryModal";
import { useHashScroll } from "@/hooks/useHashScroll";
import { brand } from "@/mocks/siteData";
import { usePublicExperiences } from "@/hooks/usePublicCatalog";
import ExperienceHero from "./components/ExperienceHero";
import ExperienceOverview from "./components/ExperienceOverview";
import ExperienceInfo from "./components/ExperienceInfo";
import ReserveSection from "./components/ReserveSection";
import RelatedExperiences from "./components/RelatedExperiences";
import StickyEnquiryBar from "@/components/feature/StickyEnquiryBar";

const parsePrice = (value: string): number | null => {
  const digits = value.replace(/[^0-9]/g, "");
  return digits ? Number(digits) : null;
};

export default function ExperienceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: items, loading, error, reload } = usePublicExperiences();
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  useHashScroll();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  const detail = useMemo(
    () => items.find((item) => item.summary.slug === slug)?.detail,
    [items, slug],
  );
  const summaries = useMemo(() => items.map((item) => item.summary), [items]);
  const details = useMemo(
    () => Object.fromEntries(items.map((item) => [item.summary.slug, item.detail])),
    [items],
  );

  if (loading && !detail) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-foreground-600">
            <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
            <p className="text-sm">Loading experience…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        {/*
          Unknown slugs must not inherit the previous page's canonical and title —
          otherwise a stale or mistyped experience URL is served as an indexable
          copy of whatever page the visitor came from (a soft 404). Marking the
          not-found state noindex keeps those URLs out of the index.
        */}
        <Seo
          title={error ? "Unable to load this experience" : `Experience not found | ${brand.name}`}
          description="We couldn't find that farm experience. Browse every Rythugaru farm and village experience in Konaseema, Andhra Pradesh instead."
          path={`/experiences/${slug ?? ""}`}
          noindex
        />
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <Reveal className="text-center max-w-lg">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-compass-3-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-6">
              {error ? "We couldn't load this experience" : "Experience not found"}
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              {error
                ? error
                : "The experience you are looking for may have moved or no longer exists. Explore all of our farm and village experiences instead."}
            </p>
            {error ? (
              <button
                type="button"
                onClick={reload}
                className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Try again
              </button>
            ) : (
              <Link
                to="/#experiences"
                className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse all experiences
                <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </Reveal>
        </main>
        <Footer />
      </div>
    );
  }

  const price = parsePrice(detail.priceFrom);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", to: "/" },
    { label: "Experiences", to: "/#experiences" },
    { label: detail.title },
  ];

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={`${detail.title} — Konaseema Farm Visit`}
        description={`${detail.tagline} — ${detail.duration}, ${detail.groupSize}. Book the ${detail.title} farm experience in Konaseema with ${brand.name}.`}
        tokens={{ title: detail.title, description: detail.tagline }}
        path={`/experiences/${detail.slug}`}
        keywords={`${detail.title}, ${detail.badge} experience, farm experience Konaseema, agri tourism Andhra Pradesh, village experience, ${brand.name}`}
        image={detail.heroImage}
        structuredData={[
          {
          "@context": "https://schema.org",
          "@type": "Product",
          name: detail.title,
          description: detail.overview[0],
          image: detail.heroImage,
          url: getAbsoluteUrl(`/experiences/${detail.slug}`),
          category: "Farm & Village Experience",
          brand: { "@type": "Brand", name: brand.name },
          provider: { "@type": "Organization", name: brand.fullName },
          areaServed: detail.location,
          ...(price
            ? {
                offers: {
                  "@type": "Offer",
                  price,
                  priceCurrency: "INR",
                  availability: "https://schema.org/InStock",
                  url: getAbsoluteUrl(`/experiences/${detail.slug}`),
                },
              }
            : {}),
          },
          buildBreadcrumbSchema(breadcrumbs, getAbsoluteUrl(`/experiences/${detail.slug}`)),
          buildFaqPageSchema(detail.faqs),
        ]}
      />
      <Navbar />
      <main>
        <ExperienceHero detail={detail} breadcrumbs={breadcrumbs} />
        <ExperienceOverview detail={detail} />
        <ExperienceInfo detail={detail} />
        <ReserveSection detail={detail} />

        <EnquirySection
          eyebrow="Plan Your Visit"
          heading="Have Questions? Let's Plan It Together"
          faqs={detail.faqs}
          panelHeading="Enquire About This Experience"
          panelIntro={`Share a few details about your group, dates and interests. Our team replies within 24 hours with availability, a clear quote and a suggested plan for your ${detail.title.toLowerCase()}.`}
          onEnquire={() => setEnquiryOpen(true)}
        />

        <RelatedExperiences currentSlug={detail.slug} experiences={summaries} details={details} />
        <BackToAll to="/experiences" label="Back to all experiences" icon="ri-compass-3-line" />
        <StickyEnquiryBar
          title={detail.title}
          eyebrow={`From ${detail.priceFrom}`}
          onEnquire={() => setEnquiryOpen(true)}
        />
        <EnquiryModal
          open={enquiryOpen}
          onClose={() => setEnquiryOpen(false)}
          kind="experience"
          options={summaries.map((item) => ({ value: item.title, label: item.title }))}
          defaultValue={detail.title}
        />
      </main>
      <Footer />
    </div>
  );
}